// Sticky top banner shown for the entire duration of a "View as this center"
// session. Two jobs: (1) make it visually impossible to forget the platform
// admin is not seeing their own data, (2) offer a one-click exit that returns
// them to /admin. Ticks a live countdown against the 60-minute token so the
// admin knows when the session is about to expire.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, LogOut } from 'lucide-react';

import { useApp } from '@/lib/store';
import { Button } from '@/components/ui/button';

function formatCountdown(ms) {
  if (ms <= 0) return 'expired';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useApp();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!impersonation) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [impersonation]);

  if (!impersonation) return null;

  // Human-friendly role label — must match the frontend's role naming
  // ("Teacher Admin" for instructors, matching the sidebar copy).
  const ROLE_LABEL = {
    org_admin: 'Center Admin',
    co_admin: 'Co-admin',
    instructor: 'Teacher Admin',
  };
  const roleLabel = ROLE_LABEL[impersonation.target_role] || 'Staff';

  const expiresMs = new Date(impersonation.expires_at).getTime() - now;
  const expired = expiresMs <= 0;

  const exit = async () => {
    setBusy(true);
    try {
      await stopImpersonation();
      navigate(`/admin/centers/${impersonation.org_id}`);
    } finally { setBusy(false); }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-3 border-b border-amber-300 bg-amber-100/95 px-4 py-2 text-amber-950 backdrop-blur md:px-6"
    >
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-amber-500 text-white">
          <Eye className="h-3.5 w-3.5" />
        </span>
        <p className="min-w-0 truncate">
          Viewing{' '}
          <b className="font-semibold" data-testid="impersonation-org-name">
            {impersonation.org_name || 'organisation'}
          </b>{' '}
          as{' '}
          <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider" data-testid="impersonation-role">
            {roleLabel}
          </span>
          {impersonation.target_name && (
            <span className="ml-1.5 text-xs text-amber-800" data-testid="impersonation-target-name">
              · {impersonation.target_name}
            </span>
          )}
          <span className="ml-2 text-xs text-amber-800">
            · session {expired ? 'expired' : `expires in ${formatCountdown(expiresMs)}`}
          </span>
        </p>
      </div>
      <Button
        size="sm"
        onClick={exit}
        disabled={busy}
        data-testid="impersonation-exit-btn"
        className="h-8 gap-1 bg-amber-950 text-amber-50 hover:bg-amber-900"
      >
        <LogOut className="h-3.5 w-3.5" />
        {busy ? 'Returning…' : 'Return to platform view'}
      </Button>
    </div>
  );
}
